/**
=========================================================
* Material Dashboard 3 PRO React - v2.4.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import swal from "sweetalert";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import CircularProgress from "@mui/material/CircularProgress";
import Autocomplete from "@mui/material/Autocomplete";

// Material Dashboard 3 PRO React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import ReportsLineChart from "examples/Charts/LineCharts/ReportsLineChart";

// Utils
import { API_ESA_OFFICE } from "utils";
import { logoutUser } from "actions/AuthAction";

// MUI components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import Icon from "@mui/material/Icon";
import MDProgress from "components/MDProgress";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

function KinerjaInstansi() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [loading, setLoading] = useState(true);
  const [agregatData, setAgregatData] = useState(null);
  const [dinasId, setDinasId] = useState(null);
  const [selectedBulan, setSelectedBulan] = useState(new Date().getMonth() + 1);
  const [selectedTahun, setSelectedTahun] = useState(new Date().getFullYear());

  // Generate tahun list (current year down to 2025)
  const tahunList = [];
  const currentYear = new Date().getFullYear();
  for (let i = currentYear; i >= 2025; i--) {
    tahunList.push(i);
  }

  // List bulan
  const bulanList = [
    { value: 1, label: "Januari" },
    { value: 2, label: "Februari" },
    { value: 3, label: "Maret" },
    { value: 4, label: "April" },
    { value: 5, label: "Mei" },
    { value: 6, label: "Juni" },
    { value: 7, label: "Juli" },
    { value: 8, label: "Agustus" },
    { value: 9, label: "September" },
    { value: 10, label: "Oktober" },
    { value: 11, label: "November" },
    { value: 12, label: "Desember" },
  ];

  // Fungsi untuk menghitung tanggal awal dan akhir berdasarkan bulan dan tahun
  const getDateRange = (bulan, tahun) => {
    const startDate = `${tahun}-${String(bulan).padStart(2, "0")}-01`;
    const endDate = new Date(tahun, bulan, 0).toISOString().split("T")[0];
    return { startDate, endDate };
  };

  // Fungsi untuk menghitung hari kerja
  const calculateWorkingDays = (start, end) => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    let workingDays = 0;
    for (
      let d = new Date(startDate);
      d <= endDate;
      d.setDate(d.getDate() + 1)
    ) {
      const dayOfWeek = d.getDay();
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        workingDays++;
      }
    }
    return workingDays;
  };

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (user) {
      setDinasId(user.dinas_id);
      fetchAgregatData(user.dinas_id, selectedBulan, selectedTahun);
    } else {
      logoutUser(navigate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  // Fetch ulang data saat bulan atau tahun berubah
  useEffect(() => {
    if (dinasId) {
      fetchAgregatData(dinasId, selectedBulan, selectedTahun);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBulan, selectedTahun]);

  const fetchAgregatData = async (currentDinasId, bulan, tahun) => {
    try {
      setLoading(true);

      // Hitung tanggal berdasarkan bulan dan tahun yang dipilih
      const { startDate, endDate } = getDateRange(bulan, tahun);
      const hariKerja = calculateWorkingDays(startDate, endDate);

      // Fetch data agregat dari backend
      const response = await axios.get(
        `${API_ESA_OFFICE}kinerja-organisasi/agregat-dinas/${currentDinasId}?bulan=${bulan}&tahun=${tahun}`
      );
      const data = response.data;

      // Ambil hari kerja yang benar dari endpoint apel (memperhitungkan hari libur)
      let hariKerjaApel = hariKerja; // Fallback ke hari kerja lokal jika gagal
      if (data.pegawai_data?.pegawai?.length > 0) {
        try {
          // Ambil hari kerja dari endpoint apel untuk pegawai pertama (semua pegawai memiliki hari kerja yang sama)
          const firstPegawai = data.pegawai_data.pegawai.find((p) => p.uid_RSS);
          if (firstPegawai?.uid_RSS) {
            const apelResponse = await axios.get(
              `${API_ESA_OFFICE}kinerja-organisasi/individu/${firstPegawai.uid_RSS}/apel?bulan=${bulan}&tahun=${tahun}`
            );
            if (apelResponse.data?.hari_kerja) {
              hariKerjaApel = apelResponse.data.hari_kerja;
            }
          }
        } catch (error) {
          console.error(
            `Error fetching hari kerja from apel endpoint for dinas ${currentDinasId}:`,
            error
          );
          // Gunakan hari kerja lokal sebagai fallback
        }
      }

      // Hitung persentase kehadiran dari endpoint eksternal
      // Menggunakan hari kerja yang sama dengan apel (memperhitungkan hari libur)
      let persentaseKehadiran = 0;
      if (
        data.pegawai_data?.pegawai?.length > 0 &&
        data.pegawai_data?.nama_dinas
      ) {
        try {
          const kehadiranResponse = await axios.post(
            "https://smartservice.rotendaokab.go.id/api/admin/daftarHadir/instansi/reguler/update",
            {
              tAwal: startDate,
              tAkhir: endDate,
              unitKerja: data.pegawai_data.nama_dinas,
            },
            {
              headers: {
                "Content-Type": "application/json",
              },
              timeout: 30000,
            }
          );

          if (
            kehadiranResponse.data.success === 1 &&
            kehadiranResponse.data.data
          ) {
            const uidRSSList = data.pegawai_data.pegawai.map((p) =>
              String(p.uid_RSS || "")
            );

            // Filter data berdasarkan uid_RSS yang ada di dinas
            const dataDinas = kehadiranResponse.data.data.filter((item) => {
              const uid = String(item.uid || item.uid_RSS || "");
              return uidRSSList.some((rss) => String(rss) === uid);
            });

            if (dataDinas.length > 0) {
              // Hitung total hari hadir dari semua pegawai di dinas
              const totalHariHadir = dataDinas.reduce(
                (sum, item) => sum + (parseInt(item.hadir) || 0),
                0
              );

              // Hitung persentase kehadiran menggunakan hari kerja dari apel (memperhitungkan hari libur)
              const totalPegawai = dataDinas.length;
              if (hariKerjaApel > 0 && totalPegawai > 0) {
                persentaseKehadiran = parseFloat(
                  (
                    (totalHariHadir / (hariKerjaApel * totalPegawai)) *
                    100
                  ).toFixed(2)
                );
              }
            }
          }
        } catch (error) {
          console.error(
            `Error fetching kehadiran data for dinas ${currentDinasId}:`,
            error
          );
          // Jika error, persentase tetap 0
        }
      }

      // Hitung ulang persentase apel menggunakan hari kerja yang benar (memperhitungkan hari libur)
      // Konsisten dengan perhitungan individu: persentase = (apel_hadir / (hari_kerja * total_pegawai)) * 100
      if (data.statistik && hariKerjaApel > 0) {
        const totalPegawaiApel = data.statistik.total_pegawai || 0;

        // Hitung ulang persentase apel pagi menggunakan hari kerja yang benar
        if (
          data.statistik.apel_pagi_hadir !== undefined &&
          totalPegawaiApel > 0
        ) {
          const expectedTotalApelPagi = hariKerjaApel * totalPegawaiApel;
          if (expectedTotalApelPagi > 0) {
            data.statistik.persentase_apel_pagi = parseFloat(
              (
                (data.statistik.apel_pagi_hadir / expectedTotalApelPagi) *
                100
              ).toFixed(2)
            );
          } else {
            data.statistik.persentase_apel_pagi = 0;
          }
        }

        // Hitung ulang persentase apel siang menggunakan hari kerja yang benar
        if (
          data.statistik.apel_siang_hadir !== undefined &&
          totalPegawaiApel > 0
        ) {
          const expectedTotalApelSiang = hariKerjaApel * totalPegawaiApel;
          if (expectedTotalApelSiang > 0) {
            data.statistik.persentase_apel_siang = parseFloat(
              (
                (data.statistik.apel_siang_hadir / expectedTotalApelSiang) *
                100
              ).toFixed(2)
            );
          } else {
            data.statistik.persentase_apel_siang = 0;
          }
        }
      }

      // Tambahkan persentase kehadiran ke statistik
      if (data.statistik) {
        data.statistik.persentase_kehadiran = persentaseKehadiran;
      }

      setAgregatData(data);
    } catch (error) {
      console.error("Error fetching agregat data:", error);
      swal({
        title: "Error",
        text: "Gagal memuat data agregat",
        icon: "error",
        button: "OK",
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="50vh"
        >
          <CircularProgress />
        </MDBox>
      </DashboardLayout>
    );
  }

  if (!agregatData) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox p={3}>
          <MDTypography variant="h5">Data tidak ditemukan</MDTypography>
        </MDBox>
      </DashboardLayout>
    );
  }

  const {
    periode,
    dinas,
    statistik,
    statistik_tamu_bulanan,
    layanan_kepuasan,
  } = agregatData;
  const profilKepala = dinas?.uid_RSS
    ? `https://smartdh.rotendaokab.go.id/uploads/${dinas.uid_RSS}.jpg`
    : null;

  // Fungsi untuk mendapatkan warna chip berdasarkan persentase
  const getChipColor = (persentase) => {
    if (persentase >= 90) return "success";
    if (persentase >= 75) return "info";
    if (persentase >= 60) return "warning";
    return "error";
  };

  // Fungsi untuk mendapatkan warna progress berdasarkan skor/nilai dengan kategori
  const getProgressColor = (value, max) => {
    const percentage = (value / max) * 100;
    // Kategori warna untuk progress bar
    if (percentage >= 90) return "success"; // Hijau - Sangat Baik
    if (percentage >= 75) return "info"; // Biru - Baik
    if (percentage >= 60) return "warning"; // Kuning - Cukup
    return "error"; // Merah - Kurang
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <MDBox mb={3}>
          <MDBox
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            gap={2}
            mb={2}
          >
            <MDTypography variant="h4" fontWeight="bold">
              Kinerja Instansi
            </MDTypography>
            <MDBox display="flex" gap={2} alignItems="center">
              <Autocomplete
                options={bulanList}
                value={bulanList.find((b) => b.value === selectedBulan) || null}
                onChange={(event, newValue) => {
                  if (newValue) setSelectedBulan(newValue.value);
                }}
                renderInput={(params) => (
                  <MDInput
                    {...params}
                    label="Bulan"
                    variant="outlined"
                    sx={{
                      backgroundColor: "white",
                      minWidth: 150,
                    }}
                  />
                )}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) =>
                  option.value === value.value
                }
              />
              <Autocomplete
                options={tahunList}
                value={selectedTahun}
                onChange={(event, newValue) => {
                  if (newValue) setSelectedTahun(newValue);
                }}
                renderInput={(params) => (
                  <MDInput
                    {...params}
                    label="Tahun"
                    variant="outlined"
                    sx={{
                      backgroundColor: "white",
                      minWidth: 120,
                    }}
                  />
                )}
                getOptionLabel={(option) => option.toString()}
              />
            </MDBox>
          </MDBox>
          <MDBox mb={2}>
            <MDTypography variant="body2" color="text">
              Periode: {bulanList.find((b) => b.value === selectedBulan)?.label}{" "}
              {selectedTahun}
            </MDTypography>
          </MDBox>
        </MDBox>

        {/* Header dengan foto kepala dinas */}
        {dinas && (
          <Card sx={{ mb: 3, boxShadow: 3 }}>
            <CardContent>
              <MDBox display="flex" alignItems="center" flexWrap="wrap" gap={2}>
                <MDBox
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  {profilKepala ? (
                    <>
                      <img
                        src={profilKepala}
                        alt={dinas.nama_kepala || "Kepala Dinas"}
                        style={{
                          width: 80,
                          height: 80,
                          borderRadius: "50%",
                          objectFit: "cover",
                          border: "3px solid #e0e0e0",
                        }}
                        onError={(e) => {
                          // Jika gambar tidak ditemukan, tampilkan icon default
                          e.target.style.display = "none";
                          if (e.target.nextSibling) {
                            e.target.nextSibling.style.display = "flex";
                          }
                        }}
                      />
                      <MDBox
                        display="none"
                        alignItems="center"
                        justifyContent="center"
                        sx={{
                          width: 80,
                          height: 80,
                          borderRadius: "50%",
                          backgroundColor: "grey.200",
                          color: "grey.500",
                        }}
                      >
                        <Icon sx={{ fontSize: 40 }}>person</Icon>
                      </MDBox>
                    </>
                  ) : (
                    <MDBox
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      sx={{
                        width: 80,
                        height: 80,
                        borderRadius: "50%",
                        backgroundColor: "grey.200",
                        color: "grey.500",
                      }}
                    >
                      <Icon sx={{ fontSize: 40 }}>person</Icon>
                    </MDBox>
                  )}
                </MDBox>
                <MDBox>
                  <MDTypography variant="h5" fontWeight="bold">
                    {dinas.nama_dinas}
                  </MDTypography>
                  {dinas.nama_kepala && (
                    <MDTypography variant="body1" color="text">
                      Kepala: {dinas.nama_kepala}
                    </MDTypography>
                  )}
                </MDBox>
              </MDBox>
            </CardContent>
          </Card>
        )}

        {/* Statistik Agregat */}
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                height: "100%",
                boxShadow: 3,
                "&:hover": {
                  boxShadow: 6,
                  transform: "translateY(-4px)",
                  transition: "all 0.3s ease",
                },
              }}
            >
              <CardContent>
                <MDBox
                  p={2}
                  borderRadius="lg"
                  sx={{
                    backgroundColor: "rgba(0, 123, 255, 0.1)",
                    textAlign: "center",
                  }}
                >
                  <MDTypography
                    variant="h4"
                    fontWeight="bold"
                    color="info"
                    mb={1}
                  >
                    {statistik?.total_pegawai || 0}
                  </MDTypography>
                  <MDTypography
                    variant="body2"
                    color="text"
                    fontWeight="medium"
                  >
                    Total Pegawai
                  </MDTypography>
                </MDBox>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                height: "100%",
                boxShadow: 3,
                "&:hover": {
                  boxShadow: 6,
                  transform: "translateY(-4px)",
                  transition: "all 0.3s ease",
                },
              }}
            >
              <CardContent>
                <MDBox
                  p={2}
                  borderRadius="lg"
                  sx={{
                    backgroundColor: "rgba(40, 167, 69, 0.1)",
                    textAlign: "center",
                  }}
                >
                  <MDTypography
                    variant="h4"
                    fontWeight="bold"
                    color="success"
                    mb={1}
                  >
                    {statistik?.total_bidang || 0}
                  </MDTypography>
                  <MDTypography
                    variant="body2"
                    color="text"
                    fontWeight="medium"
                  >
                    Total Bidang
                  </MDTypography>
                </MDBox>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                height: "100%",
                boxShadow: 3,
                "&:hover": {
                  boxShadow: 6,
                  transform: "translateY(-4px)",
                  transition: "all 0.3s ease",
                },
              }}
            >
              <CardContent>
                <MDBox
                  p={2}
                  borderRadius="lg"
                  sx={{
                    backgroundColor: "rgba(255, 193, 7, 0.1)",
                    textAlign: "center",
                  }}
                >
                  <MDTypography
                    variant="h4"
                    fontWeight="bold"
                    color="warning"
                    mb={1}
                  >
                    {statistik?.total_layanan || 0}
                  </MDTypography>
                  <MDTypography
                    variant="body2"
                    color="text"
                    fontWeight="medium"
                  >
                    Total Layanan Publik
                  </MDTypography>
                </MDBox>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                height: "100%",
                boxShadow: 3,
                "&:hover": {
                  boxShadow: 6,
                  transform: "translateY(-4px)",
                  transition: "all 0.3s ease",
                },
              }}
            >
              <CardContent>
                <MDBox
                  p={2}
                  borderRadius="lg"
                  sx={{
                    backgroundColor: "rgba(220, 53, 69, 0.1)",
                    textAlign: "center",
                  }}
                >
                  <MDTypography
                    variant="h4"
                    fontWeight="bold"
                    color="error"
                    mb={1}
                  >
                    {statistik?.total_tamu || 0}
                  </MDTypography>
                  <MDTypography
                    variant="body2"
                    color="text"
                    fontWeight="medium"
                  >
                    Total Tamu
                  </MDTypography>
                </MDBox>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                height: "100%",
                boxShadow: 3,
                "&:hover": {
                  boxShadow: 6,
                  transform: "translateY(-4px)",
                  transition: "all 0.3s ease",
                },
              }}
            >
              <CardContent>
                <MDBox
                  p={2}
                  borderRadius="lg"
                  sx={{
                    backgroundColor: "rgba(220, 53, 69, 0.1)",
                    textAlign: "center",
                  }}
                >
                  <MDTypography
                    variant="h5"
                    fontWeight="bold"
                    color={
                      (statistik?.persentase_disposisi || 0) >= 80
                        ? "success"
                        : (statistik?.persentase_disposisi || 0) >= 60
                        ? "warning"
                        : "error"
                    }
                  >
                    {statistik?.persentase_disposisi !== undefined
                      ? `${statistik.persentase_disposisi}%`
                      : "0%"}
                  </MDTypography>
                  <MDTypography
                    variant="caption"
                    color="text"
                    display="block"
                    mb={0.5}
                  >
                    Persentase Disposisi
                  </MDTypography>
                  <MDTypography variant="caption" color="text.secondary">
                    ({statistik?.total_disposisi_selesai || 0}/
                    {statistik?.total_disposisi || 0})
                  </MDTypography>
                </MDBox>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                height: "100%",
                boxShadow: 3,
                "&:hover": {
                  boxShadow: 6,
                  transform: "translateY(-4px)",
                  transition: "all 0.3s ease",
                },
              }}
            >
              <CardContent>
                <MDBox
                  p={2}
                  borderRadius="lg"
                  sx={{
                    backgroundColor: "rgba(108, 117, 125, 0.1)",
                    textAlign: "center",
                  }}
                >
                  <MDTypography
                    variant="h5"
                    fontWeight="bold"
                    color={
                      (statistik?.persentase_instruksi || 0) >= 80
                        ? "success"
                        : (statistik?.persentase_instruksi || 0) >= 60
                        ? "warning"
                        : "error"
                    }
                  >
                    {statistik?.persentase_instruksi !== undefined
                      ? `${statistik.persentase_instruksi}%`
                      : "0%"}
                  </MDTypography>
                  <MDTypography
                    variant="caption"
                    color="text"
                    display="block"
                    mb={0.5}
                  >
                    Persentase Instruksi/Tugas
                  </MDTypography>
                  <MDTypography variant="caption" color="text.secondary">
                    ({statistik?.total_instruksi_selesai || 0}/
                    {statistik?.total_instruksi || 0})
                  </MDTypography>
                </MDBox>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                height: "100%",
                boxShadow: 3,
                "&:hover": {
                  boxShadow: 6,
                  transform: "translateY(-4px)",
                  transition: "all 0.3s ease",
                },
              }}
            >
              <CardContent>
                <MDBox
                  p={2}
                  borderRadius="lg"
                  sx={{
                    backgroundColor: "rgba(23, 162, 184, 0.1)",
                    textAlign: "center",
                  }}
                >
                  <MDBox display="flex" justifyContent="center" mb={1}>
                    <Chip
                      label={
                        statistik?.persentase_kehadiran
                          ? `${statistik.persentase_kehadiran.toFixed(2)}%`
                          : "0.00%"
                      }
                      color={getChipColor(statistik?.persentase_kehadiran || 0)}
                      sx={{
                        fontSize: "1.25rem",
                        fontWeight: "bold",
                        height: 40,
                      }}
                    />
                  </MDBox>
                  <MDTypography
                    variant="body2"
                    color="text"
                    fontWeight="medium"
                  >
                    Persentase Kehadiran
                  </MDTypography>
                </MDBox>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                height: "100%",
                boxShadow: 3,
                "&:hover": {
                  boxShadow: 6,
                  transform: "translateY(-4px)",
                  transition: "all 0.3s ease",
                },
              }}
            >
              <CardContent>
                <MDBox
                  p={2}
                  borderRadius="lg"
                  sx={{
                    backgroundColor: "rgba(138, 43, 226, 0.1)",
                    textAlign: "center",
                  }}
                >
                  <MDTypography
                    variant="h4"
                    fontWeight="bold"
                    color="info"
                    mb={1}
                  >
                    {statistik?.total_event || 0}
                  </MDTypography>
                  <MDTypography
                    variant="body2"
                    color="text"
                    fontWeight="medium"
                    display="block"
                    mb={0.5}
                  >
                    Event
                  </MDTypography>
                  <MDTypography variant="caption" color="text.secondary">
                    {statistik?.total_peserta_event || 0} peserta
                  </MDTypography>
                </MDBox>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                height: "100%",
                boxShadow: 3,
                "&:hover": {
                  boxShadow: 6,
                  transform: "translateY(-4px)",
                  transition: "all 0.3s ease",
                },
              }}
            >
              <CardContent>
                <MDBox
                  p={2}
                  borderRadius="lg"
                  sx={{
                    backgroundColor: "rgba(102, 187, 106, 0.1)",
                    textAlign: "center",
                  }}
                >
                  <MDBox mb={0.5}>
                    <MDTypography
                      variant="body1"
                      fontWeight="bold"
                      color="success"
                    >
                      Pagi:{" "}
                      {statistik?.persentase_apel_pagi
                        ? `${statistik.persentase_apel_pagi.toFixed(2)}%`
                        : "0.00%"}
                    </MDTypography>
                    <MDTypography
                      variant="body1"
                      fontWeight="bold"
                      color="info"
                    >
                      Siang:{" "}
                      {statistik?.persentase_apel_siang
                        ? `${statistik.persentase_apel_siang.toFixed(2)}%`
                        : "0.00%"}
                    </MDTypography>
                  </MDBox>
                  <MDTypography
                    variant="body2"
                    color="text"
                    fontWeight="medium"
                  >
                    Persentase Apel
                  </MDTypography>
                </MDBox>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                height: "100%",
                boxShadow: 3,
                "&:hover": {
                  boxShadow: 6,
                  transform: "translateY(-4px)",
                  transition: "all 0.3s ease",
                },
              }}
            >
              <CardContent>
                <MDBox
                  p={2}
                  borderRadius="lg"
                  sx={{
                    backgroundColor: "rgba(156, 39, 176, 0.1)",
                    textAlign: "center",
                  }}
                >
                  <MDBox display="flex" justifyContent="center" mb={1}>
                    <Chip
                      label={
                        statistik?.persentase_laporan
                          ? `${statistik.persentase_laporan.toFixed(2)}%`
                          : "0.00%"
                      }
                      color={getChipColor(statistik?.persentase_laporan || 0)}
                      sx={{
                        fontSize: "1.25rem",
                        fontWeight: "bold",
                        height: 40,
                      }}
                    />
                  </MDBox>
                  <MDTypography
                    variant="body2"
                    color="text"
                    fontWeight="medium"
                  >
                    Persentase Laporan Kinerja
                  </MDTypography>
                </MDBox>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Statistik Tamu Bulanan - Grafik Garis */}
        {statistik_tamu_bulanan && statistik_tamu_bulanan.length > 0 && (
          <MDBox mt={4}>
            <ReportsLineChart
              color="error"
              title="Statistik Tamu Bulanan"
              description={`Tahun ${selectedTahun}`}
              date="Data dari Januari sampai Desember"
              chart={{
                labels: statistik_tamu_bulanan.map((b) => b.nama_bulan),
                datasets: {
                  label: "Jumlah Tamu",
                  data: statistik_tamu_bulanan.map((b) => b.total_tamu),
                },
              }}
            />
          </MDBox>
        )}

        {/* Layanan dengan Skor dan Nilai Kepuasan */}
        {layanan_kepuasan && layanan_kepuasan.length > 0 && (
          <MDBox mt={4}>
            <MDTypography variant="h5" fontWeight="bold" mb={2}>
              Layanan Publik - Skor Kepuasan
            </MDTypography>
            <Grid container spacing={3}>
              {layanan_kepuasan.map((layanan, idx) => (
                <Grid item xs={12} md={6} key={idx}>
                  <Card
                    sx={{
                      boxShadow: 3,
                      "&:hover": {
                        boxShadow: 6,
                        transform: "translateY(-4px)",
                        transition: "all 0.3s ease",
                      },
                    }}
                  >
                    <CardContent>
                      <MDTypography variant="h6" fontWeight="bold" mb={1}>
                        {layanan.layanan}
                      </MDTypography>
                      {layanan.keterangan && (
                        <MDTypography variant="body2" color="text" mb={2}>
                          {layanan.keterangan}
                        </MDTypography>
                      )}

                      {/* Skor Kepuasan (0-100) */}
                      <MDBox mb={2}>
                        <MDBox
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          mb={0.5}
                        >
                          <MDTypography variant="body2" fontWeight="medium">
                            Skor Kepuasan
                          </MDTypography>
                          <MDBox display="flex" alignItems="center" gap={1}>
                            <Chip
                              label={
                                getProgressColor(
                                  layanan.rata_rata_skor,
                                  100
                                ) === "success"
                                  ? "Sangat Baik"
                                  : getProgressColor(
                                      layanan.rata_rata_skor,
                                      100
                                    ) === "info"
                                  ? "Baik"
                                  : getProgressColor(
                                      layanan.rata_rata_skor,
                                      100
                                    ) === "warning"
                                  ? "Cukup"
                                  : "Kurang"
                              }
                              color={getProgressColor(
                                layanan.rata_rata_skor,
                                100
                              )}
                              size="small"
                            />
                            <MDTypography variant="body2" fontWeight="bold">
                              {layanan.rata_rata_skor.toFixed(2)} / 100
                            </MDTypography>
                          </MDBox>
                        </MDBox>
                        <MDProgress
                          variant="gradient"
                          value={layanan.rata_rata_skor}
                          color={getProgressColor(layanan.rata_rata_skor, 100)}
                          sx={{
                            height: 12,
                            borderRadius: 6,
                          }}
                        />
                      </MDBox>

                      {/* Nilai Kepuasan (0-4) */}
                      <MDBox mb={2}>
                        <MDBox
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          mb={0.5}
                        >
                          <MDTypography variant="body2" fontWeight="medium">
                            Nilai Kepuasan
                          </MDTypography>
                          <MDBox display="flex" alignItems="center" gap={1}>
                            <Chip
                              label={
                                getProgressColor(layanan.rata_rata_nilai, 4) ===
                                "success"
                                  ? "Sangat Baik"
                                  : getProgressColor(
                                      layanan.rata_rata_nilai,
                                      4
                                    ) === "info"
                                  ? "Baik"
                                  : getProgressColor(
                                      layanan.rata_rata_nilai,
                                      4
                                    ) === "warning"
                                  ? "Cukup"
                                  : "Kurang"
                              }
                              color={getProgressColor(
                                layanan.rata_rata_nilai,
                                4
                              )}
                              size="small"
                            />
                            <MDTypography variant="body2" fontWeight="bold">
                              {layanan.rata_rata_nilai.toFixed(2)} / 4
                            </MDTypography>
                          </MDBox>
                        </MDBox>
                        <MDProgress
                          variant="gradient"
                          value={(layanan.rata_rata_nilai / 4) * 100}
                          color={getProgressColor(layanan.rata_rata_nilai, 4)}
                          sx={{
                            height: 12,
                            borderRadius: 6,
                          }}
                        />
                      </MDBox>

                      {/* Total Responden */}
                      <MDBox
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                      >
                        <MDTypography variant="caption" color="text">
                          Total Responden: {layanan.total_responden}
                        </MDTypography>
                      </MDBox>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </MDBox>
        )}
      </MDBox>
    </DashboardLayout>
  );
}

export default KinerjaInstansi;
